import React, { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from "motion/react";
import { useAuth } from '../context/AuthContext.tsx';
import { useBranding } from '../hooks/useBranding.ts';
import { useNavigation } from '../context/NavigationContext.tsx';
import { useCompanies, useProjects, useProducts } from '@/api/queries';
import {
  LayoutDashboard, Building2, Users, Package, GitBranch, Lightbulb, FileText,
  DollarSign, BotMessageSquare, Settings, Calendar, ShieldCheck, StickyNote,
  Workflow, ChevronRight, ChevronDown, Plus, MoreHorizontal, Star, Clock, Folder,
  MoreVertical, Briefcase, Search, Shield, TrendingUp, Sparkles, Zap, User,
  FlaskConical, BarChart3
} from 'lucide-react';
import { View } from '../types';
import { NavigationItem, resolveIcon } from '../core/navigation';
import { resolveCapabilities, resolveModules } from '../core/business-profile/businessProfileResolver';
import type { BusinessCapability, BusinessProfile } from '../core/business-profile/businessProfile';

function useExpandedState(key: string, defaultValue: boolean = false) {
  const [expanded, setExpanded] = useState(() => {
    const saved = localStorage.getItem(`cyzor_sidebar_expanded_${key}`);
    if (saved !== null) return saved === 'true';
    return defaultValue;
  });

  const toggle = (e?: React.MouseEvent) => {
    if (e) {
      e.stopPropagation();
      e.preventDefault();
    }
    setExpanded(prev => {
      const next = !prev;
      localStorage.setItem(`cyzor_sidebar_expanded_${key}`, String(next));
      return next;
    });
  };

  return [expanded, toggle, setExpanded] as const;
}

export default function Sidebar({
  isCollapsed,
  toggleSidebar,
  currentView,
  setCurrentView,
  items,
  activePath
}: {
  isCollapsed: boolean,
  toggleSidebar: () => void,
  currentView: View,
  setCurrentView: (view: View) => void,
  items?: NavigationItem[],
  activePath?: string
}) {
  const { activeWorkspace, dbUser } = useAuth();
  const { iconUrl, appName } = useBranding();
  const { badges, setGlobalFilters, globalFilters } = useNavigation();
  const currentPlan = dbUser?.currentPlan || 'free';
  const businessProfile = activeWorkspace?.settings?.businessProfile as BusinessProfile | undefined;
  const resolvedModules = useMemo(
    () => businessProfile ? resolveModules(businessProfile) : null,
    [businessProfile],
  );
  const resolvedCapabilities = useMemo(
    () => businessProfile ? resolveCapabilities(businessProfile) : null,
    [businessProfile],
  );
  const moduleVisible = (moduleId: string) => !resolvedModules || resolvedModules.includes(moduleId as any);
  const capabilityVisible = (capability: BusinessCapability) => !resolvedCapabilities || resolvedCapabilities.includes(capability);

  const workspaceId = activeWorkspace?.id;
const { data: companies = [] } = useCompanies(workspaceId);
  const { data: projects = [] } = useProjects(workspaceId);
  const { data: products = [] } = useProducts();

  const [companySearch, setCompanySearch] = useState('');

  const filteredCompanies = useMemo(() => {
    if (!companySearch) return companies;
    return companies.filter((c: any) => c.name.toLowerCase().includes(companySearch.toLowerCase()));
  }, [companies, companySearch]);

  const handleNavigate = (view: View, filters?: {
    companyId?: string | number,
    projectId?: string | number,
    productId?: string | number,
    clientId?: string | number,
    documentId?: string | number,
    ideaId?: string | number
  }) => {
    if (filters) {
      setGlobalFilters(filters);
    } else {
      if (globalFilters.companyId) {
        setGlobalFilters({ companyId: globalFilters.companyId });
      } else {
        setGlobalFilters({});
      }
    }
    setCurrentView(view);
    if (!isCollapsed && window.innerWidth < 1024) toggleSidebar();
  };

  const NavItem = ({ icon: Icon, label, active, onClick, onExpand, expanded, hasChildren, badge, indent = 0, visible = true }: any) => {
    if (!visible) return null;
    return (
      <div className={`flex flex-col relative ${isCollapsed ? 'px-0 items-center py-0.5' : 'px-3'}`}>
        <div
          onClick={(e) => {
            if (onClick) onClick(e);
            else if (hasChildren && onExpand) onExpand(e);
          }}
          title={isCollapsed ? label : undefined}
          className={`group flex items-center ${
            isCollapsed
              ? 'w-10 h-10 justify-center rounded-xl mx-auto'
              : 'justify-between pr-3 py-[9px] rounded-xl'
          } cursor-pointer transition-colors duration-200 relative ${
            active
              ? isCollapsed
                ? 'bg-[#0F172A] text-white shadow-xs'
                : 'text-[#0F172A] bg-white border border-[#0F172A0F] shadow-[0_1px_3px_rgba(15,23,42,0.04)] font-bold'
              : 'text-slate-500 hover:bg-slate-200/50 hover:text-slate-900 font-medium'
          }`}
          style={!isCollapsed ? { paddingLeft: `${indent > 0 ? 14 + indent * 14 : 10}px` } : {}}
        >
          <div className={`flex items-center ${isCollapsed ? 'justify-center' : 'gap-3 overflow-hidden flex-1'}`}>
            <Icon
              size={isCollapsed ? 18 : 17}
              strokeWidth={active ? 2.2 : 1.8}
              className={`flex-shrink-0 transition-all duration-200 ${
                active
                  ? isCollapsed
                    ? 'text-white'
                    : 'text-indigo-600'
                  : 'text-slate-400 group-hover:text-slate-700'
              }`}
            />
            {!isCollapsed && (
              <span className={`text-[12.5px] tracking-tight truncate ${active ? 'font-bold text-slate-900' : 'font-semibold text-slate-600'}`}>{label}</span>
            )}
          </div>

          {!isCollapsed && active && (
            <div className="absolute right-0 top-1/2 -translate-y-1/2 w-[3px] h-4 bg-indigo-600 rounded-l-full shadow-2xs" />
          )}

          {!isCollapsed && (
            <div className="flex items-center gap-1">
              {badge && (
                <span className={`text-[9px] font-black px-2 py-0.5 rounded-full ${active ? 'bg-indigo-600 text-white shadow-sm' : 'bg-slate-200/60 text-slate-600'}`}>
                  {badge}
                </span>
              )}
              {hasChildren && (
                <ChevronDown size={14} className={`transition-transform duration-200 ${expanded ? 'rotate-0' : '-rotate-90'} ${active ? 'text-slate-900' : 'text-slate-400'}`} />
              )}
            </div>
          )}

          {isCollapsed && badge && (
            <span className={`absolute -top-0.5 -right-0.5 min-w-[15px] h-[15px] px-1 text-[8px] font-black flex items-center justify-center rounded-full ring-2 ring-[#F8FAFC] ${
              active ? 'bg-slate-900 text-white' : 'bg-indigo-600 text-white'
            }`}>
              {badge}
            </span>
          )}
        </div>
      </div>
    );
  };

  const renderDynamicNavigation = () => {
    if (!items || items.length === 0) return null;

    const renderItem = (item: NavigationItem, idx: number, indent: number = 0, showLabel: boolean = true) => {
      const Icon = resolveIcon(item.icon);
      return (
        <React.Fragment key={item.id + (indent > 0 ? `_${indent}` : '')}>
          {item.children && item.children.length > 0 ? (
            <ExpandableItem
              icon={Icon || FileText}
              label={item.label}
              active={activePath === item.path || currentView === item.id}
              onClick={() => handleNavigate(item.id as View)}
              indent={indent}
              badge={item.badge ? String(item.badge) : undefined}
            >
              {item.children.map((child) => renderItem(child, idx, indent + 1))}
            </ExpandableItem>
          ) : (
            <NavItem
              icon={Icon || FileText}
              label={item.label}
              active={activePath === item.path || currentView === item.id}
              onClick={() => handleNavigate(item.id as View)}
              indent={indent}
              badge={item.badge ? String(item.badge) : undefined}
            />
          )}
        </React.Fragment>
      );
    };

    if (isCollapsed) {
      return (
        <div className="flex flex-col gap-0.5">
          {items.map((item, idx) => renderItem(item, idx))}
        </div>
      );
    }

    return (
      <div className="flex flex-col gap-4">
        {items.map((item, idx) => {
          const Icon = resolveIcon(item.icon);
          return (
            <div key={item.id} className="flex flex-col gap-0.5">
              <div className="px-6 py-1">
                <span className="text-[10px] font-black text-[#A1A1AA] uppercase tracking-[0.15em]">{item.label.toUpperCase()}</span>
              </div>
              <div className="flex flex-col gap-0.5">
                <NavItem
                  icon={Icon || FileText}
                  label={item.label}
                  active={activePath === item.path || currentView === item.id}
                  onClick={() => handleNavigate(item.id as View)}
                  badge={item.badge ? String(item.badge) : undefined}
                />
                {item.children && item.children.map((child) => renderItem(child, idx, 1))}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const [empresasExpanded, toggleEmpresas] = useExpandedState('empresas', false);
  const [previewExpanded, togglePreview] = useExpandedState('preview', false);
  const [favoritosExpanded, toggleFavoritos] = useExpandedState('favoritos', true);
  const [recentesExpanded, toggleRecentes] = useExpandedState('recentes', true);
  const showPreviewNavigation = false;

  return (
    <>
      {!isCollapsed && (
        <div className="fixed inset-0 bg-[#0F172A]/10 backdrop-blur-[2px] z-30 lg:hidden transition-all" onClick={toggleSidebar} />
      )}

      <nav id="sidebar-nav" className={`fixed left-0 top-0 h-screen flex flex-col py-6 border-r border-[#0F172A0F] bg-white lg:rounded-r-[28px] shadow-[0_4px_24px_rgba(15,23,42,0.03)] z-40 transition-[width,transform] duration-300 overflow-hidden ${isCollapsed ? '-translate-x-full lg:translate-x-0 lg:w-[84px] px-3' : 'translate-x-0 w-[280px] px-4.5'}`}>
        {/* Ambient glow decoration */}
        <div className="absolute top-0 left-0 w-36 h-36 bg-indigo-500/5 rounded-full blur-3xl pointer-events-none" />

        {/* Workspace Brand Header */}
        <div className={`flex items-center mb-5 px-2 py-2 cursor-pointer rounded-xl hover:bg-slate-200/50 transition-colors duration-200 relative z-10 ${isCollapsed ? 'justify-center px-0' : 'gap-3'}`} onClick={() => handleNavigate('dashboard')}>
          <div className={`w-9 h-9 flex items-center justify-center flex-shrink-0 relative transition-all duration-200 ${!iconUrl ? 'bg-[#0F172A] text-white rounded-xl font-black text-base shadow-2xs' : ''}`}>
            {iconUrl ? (
              <img src={iconUrl} alt="Logo" className="w-9 h-9 object-contain" referrerPolicy="no-referrer" />
            ) : (
              <span>{appName.charAt(0)}</span>
            )}
          </div>
          {!isCollapsed && (
            <div className="flex flex-col flex-1 min-w-0">
              <div className="flex items-center gap-1.5">
                <span className="text-[14px] font-extrabold text-slate-900 tracking-tight truncate">{appName}</span>
                <span className={`text-[8px] font-bold px-1.5 py-0.5 rounded-md uppercase tracking-wider ${currentPlan === 'free' ? 'bg-amber-50 text-amber-700 border border-amber-200/80' : 'bg-slate-200/70 text-slate-700'}`}>
                  {currentPlan}
                </span>
              </div>
              <span className="text-[10px] text-slate-500 font-bold uppercase tracking-wider truncate">{activeWorkspace?.name || 'Workspace'}</span>
            </div>
          )}
        </div>

        {/* Categories */}
        <div className="flex flex-col gap-2 w-full flex-1 overflow-y-auto overflow-x-hidden custom-scrollbar pb-20">

          {/* Dynamic Navigation (App Registry) */}
          {items && items.length > 0 ? (
            renderDynamicNavigation()
          ) : (
            <>
              {isCollapsed ? (
                <div className="flex flex-col gap-0.5">
                  <NavItem icon={LayoutDashboard} label="Dashboard" active={currentView === 'dashboard'} onClick={() => handleNavigate('dashboard')} />
                  <NavItem icon={Zap} label="Pulse" active={currentView === 'pulse'} onClick={() => handleNavigate('pulse')} />
                  <NavItem icon={GitBranch} label="Projetos" visible={capabilityVisible('PROJECT_MANAGEMENT')} active={currentView === 'projetos' || currentView === 'projeto-workspace'} onClick={() => handleNavigate('projetos')} />
                  <NavItem icon={Lightbulb} label="Ideias" visible={capabilityVisible('PROJECT_MANAGEMENT')} active={currentView === 'ideias' || currentView === 'ideia-workspace'} onClick={() => handleNavigate('ideias')} />
                  <NavItem icon={Users} label="Equipe" visible={capabilityVisible('TEAM')} active={currentView === 'equipe' || currentView === 'membro-workspace'} onClick={() => handleNavigate('equipe')} />
                  <NavItem icon={Calendar} label="Agenda" visible={capabilityVisible('SCHEDULING')} active={currentView === 'agenda'} onClick={() => handleNavigate('agenda')} />
                  <NavItem icon={Users} label="Clientes" visible={capabilityVisible('CUSTOMER_MANAGEMENT')} active={currentView === 'clientes' || currentView === 'cliente-workspace'} onClick={() => handleNavigate('clientes')} />
                  <NavItem icon={TrendingUp} label="CRM & Roadmap" active={currentView === 'roadmap'} onClick={() => handleNavigate('roadmap')} />
                  <NavItem icon={Package} label="Produtos" visible={capabilityVisible('PRODUCT_CATALOG')} active={currentView === 'produtos' || currentView === 'produto-workspace'} onClick={() => handleNavigate('produtos')} />
                  <NavItem icon={DollarSign} label="Financeiro" visible={capabilityVisible('FINANCE')} active={currentView === 'financeiro'} onClick={() => handleNavigate('financeiro')} />
                  <NavItem icon={FileText} label="Documentos" visible={capabilityVisible('DOCUMENTS')} active={currentView === 'documentacao'} onClick={() => handleNavigate('documentacao')} />
                  <NavItem icon={BotMessageSquare} label="IA Intel" visible={moduleVisible('neural')} active={currentView === 'ia'} onClick={() => handleNavigate('ia')} badge={badges?.ia > 0 ? badges.ia.toString() : null} />
                  <NavItem icon={BarChart3} label="Analytics" visible={moduleVisible('bi')} active={currentView === 'ia'} onClick={() => handleNavigate('ia')} />
                  <NavItem icon={Settings} label="Configurações" active={currentView === 'configuracoes'} onClick={() => handleNavigate('configuracoes')} />
                  <NavItem icon={FlaskConical} label="Preview" active={currentView === 'empresas' || currentView === 'flow-builder' || currentView === 'keep'} onClick={() => handleNavigate('empresas')} />
                  {dbUser?.isPlatformAdmin && <NavItem icon={ShieldCheck} label="Admin" active={currentView === 'admin'} onClick={() => handleNavigate('admin')} />}
                </div>
              ) : (
                <div className="flex flex-col gap-4">
                  {/* 01. VISÃO GERAL */}
                  <div className="flex flex-col gap-0.5">
                    <div className="px-6 py-1">
                      <span className="text-[10px] font-black text-[#A1A1AA] uppercase tracking-[0.15em]">01. VISÃO GERAL</span>
                    </div>
                    <NavItem icon={LayoutDashboard} label="Dashboard" active={currentView === 'dashboard'} onClick={() => handleNavigate('dashboard')} />
                    <NavItem icon={Zap} label="Pulse" active={currentView === 'pulse'} onClick={() => handleNavigate('pulse')} />
                  </div>

                  {/* 02. OPERAÇÃO */}
                  <div className="flex flex-col gap-0.5">
                    <div className="px-6 py-1">
                      <span className="text-[10px] font-black text-[#A1A1AA] uppercase tracking-[0.15em]">02. OPERAÇÃO</span>
                    </div>
                    <NavItem icon={GitBranch} label="Projetos" visible={capabilityVisible('PROJECT_MANAGEMENT')} active={currentView === 'projetos' || currentView === 'projeto-workspace'} onClick={() => handleNavigate('projetos')} />
                    <NavItem icon={Lightbulb} label="Ideias" visible={capabilityVisible('PROJECT_MANAGEMENT')} active={currentView === 'ideias' || currentView === 'ideia-workspace'} onClick={() => handleNavigate('ideias')} />
                    <NavItem icon={Users} label="Equipe" visible={capabilityVisible('TEAM')} active={currentView === 'equipe' || currentView === 'membro-workspace'} onClick={() => handleNavigate('equipe')} />
                    <NavItem icon={Calendar} label="Agenda" visible={capabilityVisible('SCHEDULING')} active={currentView === 'agenda'} onClick={() => handleNavigate('agenda')} />
                  </div>

                  {/* 03. RELACIONAMENTO */}
                  <div className="flex flex-col gap-0.5">
                    <div className="px-6 py-1">
                      <span className="text-[10px] font-black text-[#A1A1AA] uppercase tracking-[0.15em]">03. RELACIONAMENTO</span>
                    </div>
                    <NavItem icon={Users} label="Clientes" visible={capabilityVisible('CUSTOMER_MANAGEMENT')} active={currentView === 'clientes' || currentView === 'cliente-workspace'} onClick={() => handleNavigate('clientes')} />
                    <NavItem icon={TrendingUp} label="CRM" visible={capabilityVisible('CUSTOMER_MANAGEMENT')} active={currentView === 'roadmap'} onClick={() => handleNavigate('roadmap')} />
                  </div>

                  {/* 04. NEGÓCIOS */}
                  <div className="flex flex-col gap-0.5">
                    <div className="px-6 py-1">
                      <span className="text-[10px] font-black text-[#A1A1AA] uppercase tracking-[0.15em]">04. NEGÓCIOS</span>
                    </div>
                    <NavItem icon={Package} label="Produtos" visible={capabilityVisible('PRODUCT_CATALOG')} active={currentView === 'produtos' || currentView === 'produto-workspace'} onClick={() => handleNavigate('produtos')} />
                    <NavItem icon={DollarSign} label="Financeiro" visible={capabilityVisible('FINANCE')} active={currentView === 'financeiro'} onClick={() => handleNavigate('financeiro')} />
                    <NavItem icon={FileText} label="Documentos" visible={capabilityVisible('DOCUMENTS')} active={currentView === 'documentacao'} onClick={() => handleNavigate('documentacao')} />
                  </div>

                  {/* 05. INTELIGÊNCIA */}
                  <div className="flex flex-col gap-0.5">
                    <div className="px-6 py-1">
                      <span className="text-[10px] font-black text-[#A1A1AA] uppercase tracking-[0.15em]">05. INTELIGÊNCIA</span>
                    </div>
                    <NavItem icon={BotMessageSquare} label="IA" visible={moduleVisible('neural')} active={currentView === 'ia'} onClick={() => handleNavigate('ia')} badge={badges?.ia > 0 ? badges.ia.toString() : null} />
                    <NavItem icon={BarChart3} label="Analytics" visible={moduleVisible('bi')} active={currentView === 'ia'} onClick={() => handleNavigate('ia')} />
                  </div>

                  {/* 06. ADMINISTRAÇÃO */}
                  <div className="flex flex-col gap-0.5">
                    <div className="px-6 py-1">
                      <span className="text-[10px] font-black text-[#A1A1AA] uppercase tracking-[0.15em]">06. ADMINISTRAÇÃO</span>
                    </div>
                    <NavItem icon={Settings} label="Configurações" active={currentView === 'configuracoes'} onClick={() => handleNavigate('configuracoes')} />
                    <NavItem icon={User} label="Perfil Executivo" active={currentView === 'perfil'} onClick={() => handleNavigate('perfil')} />
                    {dbUser?.isPlatformAdmin && <NavItem icon={ShieldCheck} label="Admin" active={currentView === 'admin'} onClick={() => handleNavigate('admin')} />}
                  </div>

                  {/* 07. PREVIEW (COLLAPSIBLE, CLOSED BY DEFAULT) */}
                  {showPreviewNavigation && <div className="flex flex-col gap-0.5">
                    <div
                      className="flex items-center justify-between px-6 py-1.5 cursor-pointer text-[#A1A1AA] hover:text-[#18181B] group transition-colors select-none"
                      onClick={togglePreview}
                    >
                      <div className="flex items-center gap-2">
                        <FlaskConical size={13} className="text-[#A1A1AA]" />
                        <span className="text-[10px] font-black uppercase tracking-[0.15em]">07. PREVIEW</span>
                      </div>
                      <div className="flex items-center gap-1">
                        {previewExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                      </div>
                    </div>

                    <AnimatePresence>
                      {previewExpanded && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: 'auto', opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          className="flex flex-col gap-0.5 pt-1"
                        >
                          <NavItem icon={Building2} label="Empresas" active={currentView === 'empresas'} onClick={() => { setGlobalFilters({}); setCurrentView('empresas'); }} />
                          <NavItem icon={Workflow} label="Flow Builder" active={currentView === 'flow-builder'} onClick={() => handleNavigate('flow-builder')} />
                          <NavItem icon={StickyNote} label="Keep Notas" active={currentView === 'keep'} onClick={() => handleNavigate('keep')} />

                          {/* Árvore de Empresas & Produtos (Fluxo Antigo) */}
                          <div className="mt-2 pt-2 border-t border-slate-100 dark:border-slate-800">
                            <div className="px-6 py-1">
                              <span className="text-[9px] font-black text-[#A1A1AA] uppercase tracking-widest">Árvore de Empresas</span>
                            </div>
                            <div className="px-5 py-2 mb-1">
                              <div className="relative flex items-center w-full">
                                <Search size={14} className="absolute left-3 text-slate-400" />
                                <input
                                  type="text"
                                  placeholder="Pesquisar..."
                                  value={companySearch}
                                  onChange={(e) => setCompanySearch(e.target.value)}
                                  className="w-full bg-white/70 dark:bg-slate-800/70 backdrop-blur-sm border border-slate-200/60 dark:border-slate-700 text-[12px] rounded-xl pl-9 pr-3 py-1.5 focus:outline-none text-slate-800 dark:text-slate-200 placeholder:text-slate-400 transition-all shadow-xs"
                                />
                              </div>
                            </div>

                            {filteredCompanies.length === 0 && (
                              <div className="text-[11px] text-slate-400 px-6 py-1 font-medium">Nenhuma empresa encontrada.</div>
                            )}
                            {filteredCompanies.map((company: any) => (
                              <CompanyNode
                                key={company.id}
                                company={company}
                                projects={projects.filter((p: any) => p.companyId === company.id)}
                                products={products.filter((p: any) => p.companyId === company.id)}
                                currentView={currentView}
                                globalFilters={globalFilters}
                                handleNavigate={handleNavigate}
                              />
                            ))}
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>}
                </div>
              )}
            </>
          )}
        </div>

        {/* Bottom Profile */}
        <div className={`mt-auto pt-4 border-t border-slate-200/50 ${isCollapsed ? 'px-0' : 'px-1'}`}>
          {!isCollapsed ? (
            <div className="flex flex-col gap-3">
              <div
                onClick={() => handleNavigate('configuracoes')}
                className="relative overflow-hidden p-4 rounded-[24px] cursor-pointer bg-gradient-to-br from-indigo-950 via-indigo-900 to-slate-900 text-white border border-indigo-500/20 shadow-lg hover:shadow-xl hover:scale-[1.01] transition-all duration-300 group"
              >
                <div className="absolute top-0 right-0 w-24 h-24 bg-indigo-500/20 rounded-full blur-2xl pointer-events-none group-hover:bg-indigo-500/35 transition-all duration-300" />

                <div className="flex flex-col gap-2 relative z-10">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-1.5">
                      <div className="w-6 h-6 rounded-lg bg-indigo-500/20 border border-indigo-400/30 flex items-center justify-center">
                        <Sparkles size={12} className="text-indigo-300 animate-pulse" />
                      </div>
                      <span className="text-[10px] font-black uppercase tracking-[0.15em] text-indigo-300">CYZOR PRO</span>
                    </div>
                    <span className="text-[8px] font-black px-1.5 py-0.5 rounded-md bg-indigo-600 text-white uppercase tracking-wider shadow-sm">Upgrade</span>
                  </div>

                  <div className="mt-0.5">
                    <h4 className="text-[12px] font-black text-white leading-tight">Gestão de Alta Performance</h4>
                    <p className="text-[9.5px] text-indigo-200/80 font-semibold mt-0.5 leading-snug">Desbloqueie IA inteligente, automações avançadas e controladoria multi-empresas hoje.</p>
                  </div>

                  <div className="mt-1.5 flex items-center justify-between py-1.5 px-3 rounded-xl bg-white/10 hover:bg-white/15 border border-white/5 transition-all text-center">
                    <span className="text-[9px] font-black uppercase tracking-wider text-white">Seja PRO Agora</span>
                    <ChevronRight size={10} className="text-indigo-300 group-hover:translate-x-1 transition-transform" />
                  </div>
                </div>
              </div>

              <div className="px-1 pb-1">
                <button className="w-full flex items-center gap-3 py-2.5 px-3.5 rounded-2xl text-slate-500 hover:bg-rose-50 hover:text-rose-600 transition-all font-bold text-[12.5px]">
                  <Settings size={18} className="rotate-90" />
                  <span>Sair</span>
                </button>
              </div>
            </div>
          ) : (
            <div className="flex flex-col items-center gap-4 py-2">
              <div className="w-10 h-10 rounded-full bg-slate-900 text-white flex items-center justify-center text-sm font-black cursor-pointer hover:scale-105 transition-all shadow-sm">
                {dbUser?.name ? dbUser.name.charAt(0).toUpperCase() : 'U'}
              </div>
              <button className="text-slate-400 hover:text-rose-600 transition-colors">
                <Settings size={20} className="rotate-90" />
              </button>
            </div>
          )}
        </div>
      </nav>
    </>
  );
}

// Simple expandable wrapper for nested app registry items
function ExpandableItem({ icon: Icon, label, active, onClick, children, indent = 0, badge }: any) {
  const [expanded, toggle, setExpanded] = useExpandedState(`expand_${label}_${indent}`, false);

  return (
    <div className="flex flex-col">
      <div
        className={`flex items-center justify-between py-[9px] pr-3 rounded-xl cursor-pointer transition-colors duration-200 relative ${
          active
            ? 'text-[#0F172A] bg-white border border-[#0F172A0F] shadow-[0_1px_3px_rgba(15,23,42,0.04)] font-bold'
            : 'text-slate-500 hover:bg-slate-200/50 hover:text-slate-900 font-medium'
        }`}
        style={{ paddingLeft: `${14 + indent * 14}px` }}
        onClick={(e) => {
          setExpanded(!expanded);
          if (onClick) onClick(e);
        }}
      >
        <div className="flex items-center gap-3 overflow-hidden flex-1">
          <Icon size={17} strokeWidth={active ? 2.2 : 1.8} className={`flex-shrink-0 ${active ? 'text-indigo-600' : 'text-slate-400 group-hover:text-slate-700'}`} />
          <span className={`text-[12.5px] tracking-tight truncate ${active ? 'font-bold text-slate-900' : 'font-semibold text-slate-600'}`}>{label}</span>
        </div>
        {badge && (
          <span className={`text-[9px] font-black px-2 py-0.5 rounded-full ${active ? 'bg-indigo-600 text-white' : 'bg-slate-200/60 text-slate-600'}`}>
            {badge}
          </span>
        )}
        <ChevronDown size={14} className={`transition-transform duration-200 ${expanded ? 'rotate-0' : '-rotate-90'} ${active ? 'text-slate-900' : 'text-slate-400'}`} />
      </div>
      <div className={`overflow-hidden transition-all duration-200 ${expanded ? 'max-h-[1000px] opacity-100' : 'max-h-0 opacity-0'}`}>
        {children}
      </div>
    </div>
  );
}

// Subcomponents for recursion - legacy tree view
function CompanyNode({ company, projects, products, currentView, globalFilters, handleNavigate }: any) {
  const [expanded, toggle, setExpanded] = useExpandedState(`company_${company.id}`, false);
  const isActive = currentView === 'empresas' && globalFilters.companyId === company.id;

  return (
    <div className="flex flex-col px-3">
      <div
        className={`group flex items-center justify-between py-[11px] pr-3 pl-3 rounded-2xl cursor-pointer transition-colors duration-300 relative ${
          isActive
            ? 'text-slate-900 bg-white/80 border border-white shadow-sm font-black'
            : 'text-slate-500 hover:bg-white/60 hover:text-slate-900'
        }`}
        onClick={() => {
          setExpanded(!expanded);
          handleNavigate('empresas', { companyId: company.id });
        }}
      >
        <div className="flex items-center gap-3.5 overflow-hidden flex-1">
          <Building2 size={19} className={`flex-shrink-0 ${isActive ? 'text-indigo-600' : 'text-slate-400'}`} />
          <span className={`text-[13px] tracking-tight truncate ${isActive ? 'font-black text-slate-900' : 'font-semibold text-slate-600'}`}>{company.name}</span>
        </div>
        <div className="flex items-center gap-1.5">
          {isActive && (
            <div className="absolute right-[-18px] top-1/2 -translate-y-1/2 w-[4px] h-5 bg-indigo-600 rounded-l-full shadow-[0_0_12px_rgba(79,70,229,0.5)]" />
          )}
          <ChevronDown size={14} className={`transition-transform duration-200 ${expanded ? 'rotate-0' : '-rotate-90'} ${isActive ? 'text-slate-900' : 'text-slate-400'}`} />
        </div>
      </div>

      <AnimatePresence>
        {expanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="flex flex-col gap-0.5 mt-0.5 mb-2 relative before:absolute before:left-[22px] before:top-0 before:bottom-3 before:w-[1.5px] before:bg-slate-200/80 before:z-10"
          >
            <SubNode icon={GitBranch} label="Projetos" count={projects.length} onClick={() => handleNavigate('projetos', { companyId: company.id })} active={currentView === 'projetos' && globalFilters.companyId === company.id} items={projects} itemType="projetos" handleNavigate={handleNavigate} companyId={company.id} />
            <SubNode icon={Package} label="Produtos" count={products.length} onClick={() => handleNavigate('produtos', { companyId: company.id })} active={currentView === 'produtos' && globalFilters.companyId === company.id} items={products} itemType="produtos" handleNavigate={handleNavigate} companyId={company.id} />
            <LeafNode icon={Users} label="Clientes" onClick={() => handleNavigate('clientes', { companyId: company.id })} active={currentView === 'clientes' && globalFilters.companyId === company.id} />
            <LeafNode icon={DollarSign} label="Financeiro" onClick={() => handleNavigate('financeiro', { companyId: company.id })} active={currentView === 'financeiro' && globalFilters.companyId === company.id} />
            <LeafNode icon={FileText} label="Documentos" onClick={() => handleNavigate('documentacao', { companyId: company.id })} active={currentView === 'documentacao' && globalFilters.companyId === company.id} />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function SubNode({ icon: Icon, label, count, onClick, active, items, itemType, handleNavigate, companyId }: any) {
  const [expanded, toggle, setExpanded] = useExpandedState(`sub_${label}_${companyId}`, false);

  return (
    <div className="flex flex-col relative">
      <div
        className={`group flex items-center justify-between py-2 pr-3 pl-[35px] rounded-xl cursor-pointer transition-colors duration-200 relative z-0 ${
          active
            ? 'text-slate-900 bg-white/80 font-bold border border-white shadow-sm'
            : 'text-slate-500 hover:bg-white/60'
        }`}
        onClick={(e) => {
          setExpanded(!expanded);
          if (onClick) onClick(e);
        }}
      >
        <div className="absolute left-[22px] top-1/2 -translate-y-1/2 w-[13px] h-[1.5px] bg-slate-200 z-10" />
        <div className="flex items-center gap-3 overflow-hidden flex-1 relative z-20">
          <Icon size={17} className={`flex-shrink-0 ${active ? 'text-indigo-600' : 'text-slate-400'}`} />
          <span className={`text-[13px] tracking-tight truncate ${active ? 'font-black text-slate-900' : 'font-semibold text-slate-600'}`}>{label}</span>
        </div>
        <div className="flex items-center gap-1.5">
          {count > 0 && !active && (
            <span className="text-[10px] font-black px-1.5 py-0.5 rounded-full bg-slate-200/60 text-slate-600">
              {count}
            </span>
          )}
          <ChevronDown size={14} className={`transition-transform duration-200 ${expanded ? 'rotate-0' : '-rotate-90'} ${active ? 'text-slate-900' : 'text-slate-400'}`} />
        </div>
      </div>

      <AnimatePresence>
        {expanded && Array.isArray(items) && items.length > 0 && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="flex flex-col gap-0.5 pb-1 relative before:absolute before:left-[51px] before:top-0 before:bottom-3 before:w-[1.5px] before:bg-slate-200/80 before:z-10"
          >
            {items.map((item: any) => {
              const itemFilters: any = { companyId };
              if (itemType === 'projetos') itemFilters.projectId = item.id;
              else if (itemType === 'produtos') itemFilters.productId = item.id;

              return (
                <LeafNode
                  key={item.id}
                  icon={Folder}
                  label={item.name}
                  indent={1}
                  onClick={() => handleNavigate(itemType, itemFilters)}
                  active={false}
                  parentIsSubNode={true}
                />
              );
            })}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function LeafNode({ icon: Icon, label, indent = 0, onClick, active, parentIsSubNode = false }: any) {
  return (
    <div
      className={`group flex items-center justify-between py-2 pr-3 rounded-xl cursor-pointer transition-colors duration-200 relative z-0 ${
        active ? 'text-slate-900 font-bold bg-white/80 border border-white shadow-sm' : 'text-slate-500 hover:bg-white/60'
      }`}
      style={{ paddingLeft: `${parentIsSubNode ? 64 : 35}px` }}
      onClick={onClick}
    >
      <div className="absolute left-[22px] top-1/2 -translate-y-1/2 w-[13px] h-[1.5px] bg-slate-200 z-10" style={parentIsSubNode ? { left: '51px' } : {}} />
      <div className="flex items-center gap-3 overflow-hidden flex-1 relative z-20">
        <Icon size={15} className={`flex-shrink-0 ${active ? 'text-indigo-600' : 'text-slate-400'}`} />
        <span className={`text-[12.5px] tracking-tight truncate ${active ? 'font-black text-slate-900' : 'font-semibold text-slate-600'}`}>{label}</span>
      </div>
    </div>
  );
}
